import {
  createAlcoholCurve,
  drinkingDayWindow,
  estimateAlcoholAt,
  pureAlcoholGrams,
  type DrinkEvent,
  type Group,
  type Participant,
} from '@lbc/shared';

export interface AchievementInput {
  group: Pick<Group, 'timezone' | 'dayBoundaryHour'>;
  participants: ReadonlyArray<Participant>;
  participant: Participant;
  events: ReadonlyArray<DrinkEvent>;
  now?: Date;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  unlocked: boolean;
  progress: string;
  detail: string;
  progressValue?: number;
  progressTotal?: number;
}

interface DayMetrics {
  start: Date;
  end: Date;
  alcoholGrams: Map<string, number>;
  drinkCounts: Map<string, number>;
  alcoholPeaks: Map<string, number>;
  morningEstimates: Map<string, number>;
}

const PROMILLE_LEVELS = [
  { id: 'gold-level', title: 'Gold-Pegel', threshold: 3, medal: 'Gold' },
  { id: 'silver-level', title: 'Silber-Pegel', threshold: 2, medal: 'Silber' },
  { id: 'bronze-level', title: 'Bronze-Pegel', threshold: 1, medal: 'Bronze' },
] as const;

export function createAchievements(input: AchievementInput): Achievement[] {
  const now = input.now ?? new Date();
  const evaluatedAt = latestAchievementEvaluationAt(now, input.group.timezone);
  const activeEvents = input.events.filter((event) => {
    const occurredAt = new Date(event.occurredAt).getTime();
    return (
      event.deletedAt === null && Number.isFinite(occurredAt) && occurredAt < evaluatedAt.getTime()
    );
  });
  const ownId = input.participant.id;
  const participants = [
    ...input.participants.filter((participant) => participant.id !== ownId),
    input.participant,
  ];
  const allDays = createDayMetrics(input, participants, activeEvents, evaluatedAt);
  const days = allDays.filter((day) => day.start.getTime() < evaluatedAt.getTime());
  const ownDays = days.filter(
    (day) => (day.drinkCounts.get(ownId) ?? 0) > 0 || (day.alcoholPeaks.get(ownId) ?? 0) > 0,
  );

  const dailyAlcoholWinner = ownDays.find((day) => isOwnWinner(day.alcoholGrams, ownId));
  const dailyDrinkWinner = ownDays.find((day) => isOwnWinner(day.drinkCounts, ownId));
  const weeklyStreak = findWeeklyStreak(days, ownId);
  const currentStreak = weeklyStreak === undefined ? trailingStreak(days, ownId, 0.5) : 7;
  const highestPeak = Math.max(0, ...days.map((day) => day.alcoholPeaks.get(ownId) ?? 0));
  const highestMorningEstimate = Math.max(
    0,
    ...allDays.map((day) => day.morningEstimates.get(ownId) ?? 0),
  );

  const achievements: Achievement[] = [
    {
      id: 'daily-most-alcohol',
      title: 'Tagesvollste:r',
      description: 'An einem Tag hattest du die größte Alkoholmenge der Gruppe in dir.',
      unlocked: dailyAlcoholWinner !== undefined,
      progress:
        dailyAlcoholWinner === undefined
          ? 'Noch kein Tagessieg'
          : `Gewonnen am ${formatDay(dailyAlcoholWinner.start, input.group.timezone)}`,
      detail:
        dailyAlcoholWinner === undefined
          ? 'Sammle an einem Trinktag mehr reinen Alkohol als alle anderen.'
          : `${formatGrams(dailyAlcoholWinner.alcoholGrams.get(ownId) ?? 0)} g reiner Alkohol an diesem Tag`,
    },
    {
      id: 'daily-most-drinks',
      title: 'Getränkemeister:in',
      description: 'Du hast an einem Tag die meisten Getränke der Gruppe konsumiert.',
      unlocked: dailyDrinkWinner !== undefined,
      progress:
        dailyDrinkWinner === undefined
          ? 'Noch kein Tagessieg'
          : `Gewonnen am ${formatDay(dailyDrinkWinner.start, input.group.timezone)}`,
      detail:
        dailyDrinkWinner === undefined
          ? 'Sammle an einem Trinktag mehr Getränke als alle anderen.'
          : `${dailyDrinkWinner.drinkCounts.get(ownId) ?? 0} Getränke an diesem Tag`,
    },
    {
      id: 'seven-day-half-promille',
      title: 'Sieben-Tage-Pegel',
      description: 'In einer Woche lag dein Tageshöchstwert jeden Tag über 0,5 ‰.',
      unlocked: weeklyStreak !== undefined,
      progress:
        weeklyStreak === undefined
          ? `${currentStreak}/7 Tage über 0,5 ‰`
          : `Erreicht vom ${formatDay(weeklyStreak.start, input.group.timezone)} bis ${formatDay(weeklyStreak.end, input.group.timezone)}`,
      progressValue: currentStreak,
      progressTotal: 7,
      detail:
        weeklyStreak === undefined
          ? 'Halte deinen Tageshöchstwert an sieben aufeinanderfolgenden Trinktagen über 0,5 ‰.'
          : 'Sieben aufeinanderfolgende Trinktage geschafft',
    },
    ...PROMILLE_LEVELS.map(({ id, title, threshold, medal }) => ({
      id,
      title,
      description: `${medal}: Dein Tageshöchstwert lag über ${formatPermille(threshold)} ‰.`,
      unlocked: highestPeak >= threshold,
      progress: `Höchster Tagespegel: ${formatPermille(highestPeak)} ‰`,
      detail:
        highestPeak >= threshold
          ? `Stufe ${medal} erreicht`
          : `Noch ${formatPermille(threshold - highestPeak)} ‰ bis ${medal}`,
    })),
    {
      id: 'morning-rest-alcohol',
      title: 'Morgen-Restpegel',
      description: 'Um 06:00 Uhr hattest du noch mehr als 1 ‰ Restalkohol.',
      unlocked: highestMorningEstimate > 1,
      progress: `Höchster 06:00-Wert: ${formatPermille(highestMorningEstimate)} ‰`,
      detail:
        highestMorningEstimate > 1
          ? 'Restalkohol am Morgen über 1 ‰ erreicht'
          : 'Noch kein Morgenwert über 1 ‰',
    },
  ];

  return achievements;
}

function latestAchievementEvaluationAt(now: Date, timezone: string): Date {
  return drinkingDayWindow(now, timezone, 6).start;
}

function createDayMetrics(
  input: AchievementInput,
  participants: ReadonlyArray<Participant>,
  events: ReadonlyArray<DrinkEvent>,
  now: Date,
): DayMetrics[] {
  const earliestEventAt = Math.min(...events.map((event) => new Date(event.occurredAt).getTime()));
  let range = drinkingDayWindow(now, input.group.timezone, input.group.dayBoundaryHour);
  const ranges = [range];
  while (Number.isFinite(earliestEventAt) && range.start.getTime() > earliestEventAt) {
    range = drinkingDayWindow(
      new Date(range.start.getTime() - 1),
      input.group.timezone,
      input.group.dayBoundaryHour,
    );
    ranges.unshift(range);
  }

  return ranges.map((day) => {
    const calculatedAt = new Date(Math.min(now.getTime(), day.end.getTime()));
    const dayEvents = events.filter((event) => {
      const occurredAt = new Date(event.occurredAt).getTime();
      return occurredAt >= day.start.getTime() && occurredAt <= calculatedAt.getTime();
    });
    const alcoholGrams = new Map<string, number>();
    const drinkCounts = new Map<string, number>();
    for (const event of dayEvents) {
      alcoholGrams.set(
        event.participantId,
        (alcoholGrams.get(event.participantId) ?? 0) + pureAlcoholGrams(event.volumeMl, event.abv),
      );
      drinkCounts.set(event.participantId, (drinkCounts.get(event.participantId) ?? 0) + 1);
    }

    const alcoholPeaks = new Map<string, number>();
    const morningEstimates = new Map<string, number>();
    for (const participant of participants) {
      if (participant.weightKg === undefined) {
        alcoholPeaks.set(participant.id, 0);
        morningEstimates.set(participant.id, 0);
        continue;
      }
      const profile = participantProfile(participant);
      const participantEvents = events.filter((event) => event.participantId === participant.id);
      const eventsThroughDay = participantEvents.filter(
        (event) => new Date(event.occurredAt).getTime() <= calculatedAt.getTime(),
      );
      const points = createAlcoholCurve(eventsThroughDay, profile, day.start, calculatedAt);
      alcoholPeaks.set(
        participant.id,
        Math.max(
          estimateAlcoholAt(eventsThroughDay, profile, calculatedAt),
          ...points.map((point) => point.estimatedPermille),
        ),
      );
      morningEstimates.set(
        participant.id,
        estimateAlcoholAt(
          participantEvents.filter(
            (event) => new Date(event.occurredAt).getTime() <= day.start.getTime(),
          ),
          profile,
          day.start,
        ),
      );
    }

    return {
      start: day.start,
      end: day.end,
      alcoholGrams,
      drinkCounts,
      alcoholPeaks,
      morningEstimates,
    };
  });
}

function participantProfile(participant: Participant) {
  return {
    weightKg: participant.weightKg!,
    ...(participant.heightCm === undefined ? {} : { heightCm: participant.heightCm }),
    ...(participant.distributionFactor === null || participant.distributionFactor === undefined
      ? {}
      : { distributionFactor: participant.distributionFactor }),
    ...(participant.eliminationRate === null || participant.eliminationRate === undefined
      ? {}
      : { eliminationRate: participant.eliminationRate }),
  };
}

function isOwnWinner(values: ReadonlyMap<string, number>, ownId: string): boolean {
  const ownValue = values.get(ownId) ?? 0;
  if (ownValue <= 0) {
    return false;
  }
  return [...values.entries()].every(([participantId, value]) => {
    return participantId === ownId || ownValue > value;
  });
}

function findWeeklyStreak(
  days: ReadonlyArray<DayMetrics>,
  ownId: string,
): { start: Date; end: Date } | undefined {
  for (let index = 0; index <= days.length - 7; index += 1) {
    const week = days.slice(index, index + 7);
    if (week.every((day) => (day.alcoholPeaks.get(ownId) ?? 0) > 0.5)) {
      return { start: week[0]!.start, end: week[6]!.start };
    }
  }
  return undefined;
}

function trailingStreak(days: ReadonlyArray<DayMetrics>, ownId: string, threshold: number): number {
  let current = 0;
  for (let index = days.length - 1; index >= 0; index -= 1) {
    const day = days[index];
    if (day === undefined) {
      break;
    }
    if ((day.alcoholPeaks.get(ownId) ?? 0) > threshold) {
      current += 1;
    } else {
      break;
    }
  }
  return current;
}

function formatDay(at: Date, timezone: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeZone: timezone,
  }).format(at);
}

function formatPermille(value: number): string {
  return Math.max(0, value).toFixed(2);
}

function formatGrams(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}
